import Dashboard from "@/packages/dashboard/App";
import React from "react";
import { createRoot } from "react-dom/client";

// Define a mapping of component names to actual components
const componentMap: Record<string, React.ComponentType<any>> = {
  Dashboard,
};

// Function to find and mount React components
const mountReactComponents = () => {
  console.log("Searching for React components to mount...");
  const mountPoints = document.querySelectorAll("[data-react-component]");
  console.log(`Found ${mountPoints.length} potential mount points.`);

  mountPoints.forEach((mountPoint) => {
    // Avoid re-initializing if already mounted (simple check)
    if (mountPoint.getAttribute("data-react-mounted")) {
      console.log(`Skipping already mounted component on element:`, mountPoint);
      return;
    }

    const componentName = mountPoint.getAttribute("data-react-component");
    const propsString = mountPoint.getAttribute("data-react-props");

    console.log(`Processing mount point for component: ${componentName}`);

    if (!componentName) {
      console.error("Mount point is missing data-react-component attribute.", mountPoint);
      return;
    }

    if (!componentMap[componentName]) {
      console.error(
        `React component "${componentName}" not found in componentMap. Available components: ${Object.keys(componentMap).join(", ")}`,
      );
      return;
    }

    // Parse the props
    let props = {};
    if (propsString) {
      try {
        // Decode HTML entities potentially introduced by ERB interpolation
        const decodedPropsString = propsString
          .replace(/&quot;/g, '"')
          .replace(/&amp;/g, "&")
          .replace(/&lt;/g, "<")
          .replace(/&gt;/g, ">");
        props = JSON.parse(decodedPropsString);
        console.log(`Parsed props for ${componentName}:`, props);
      } catch (e) {
        console.error(`Failed to parse props for ${componentName}:`, e, "Raw props string:", propsString);
        // Optionally render an error state in the component or skip rendering
        return;
      }
    } else {
      console.log(`No props string found for ${componentName}. Passing empty props object.`);
    }

    // Render the component
    try {
      const Component = componentMap[componentName];
      const root = createRoot(mountPoint);
      root.render(
        <React.StrictMode>
          <Component {...props} />
        </React.StrictMode>,
      );
      console.log(`Successfully rendered ${componentName} component.`);
    } catch (renderError) {
      console.error(`Error rendering component ${componentName}:`, renderError);
      // Optionally display an error message in the mount point itself
      mountPoint.innerHTML = `<div style="color: red; border: 1px solid red; padding: 10px;">Error rendering ${componentName}. Check console for details.</div>`;
    }

    // Mark as mounted to prevent re-initialization on subsequent turbo:load events
    // if the element itself wasn't replaced.
    mountPoint.setAttribute("data-react-mounted", "true");
  });
};

// Run the mounting function on initial page load and Turbo navigations
document.addEventListener("DOMContentLoaded", mountReactComponents);
document.addEventListener("turbo:load", mountReactComponents);


// Optional: Add cleanup for Turbo Drive navigations to unmount components
// This prevents memory leaks if components hold state or subscriptions.
// We need to keep track of the created roots to unmount them.
// Let's defer this unless needed, as the current approach might be sufficient.
/*
const roots = new Map(); // Store roots keyed by mountPoint element

// ... modify mountReactComponents to store the root in the map ...
// root.render(...);
// roots.set(mountPoint, root);
// mountPoint.setAttribute("data-react-mounted", "true");


document.addEventListener("turbo:before-cache", () => {
    console.log("Turbo Drive is caching the page. Unmounting React components.");
    roots.forEach((root, mountPoint) => {
        try {
            root.unmount();
            console.log("Unmounted component:", mountPoint.getAttribute("data-react-component"));
            mountPoint.removeAttribute("data-react-mounted"); // Allow re-mounting later
        } catch (e) {
            console.error("Error unmounting React component:", e);
        }
    });
    roots.clear(); // Clear the map after unmounting
});
*/
